import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { KeycloakService } from 'keycloak-angular';
import { Observable, switchMap } from 'rxjs';

import { PostHogService } from '@core/analytics/posthog.service';
import { refreshAccessToken$ } from '@core/helpers/refresh-access-token';
import { CONFIG_TOKEN } from '@core/tokens';

import { AiStreamFrame } from '@domain/ai-assistant/ai-assistant-stream.models';
import { SendConversationInputRequest } from '@domain/ai-assistant/ai-assistant.models';

import { readEventStream } from './helpers/sse-parser';

@Injectable({
  providedIn: 'root',
})
export class AiAssistantStreamService {
  readonly #rootUrl = inject(CONFIG_TOKEN).backendRootUrl;
  readonly #keycloakService = inject(KeycloakService);
  readonly #store = inject(Store);
  readonly #posthogService = inject(PostHogService);
  readonly #traceIdsByConversation = new Map<string, string>();

  public streamConversationInput$(conversationId: string, body: SendConversationInputRequest): Observable<AiStreamFrame> {
    return refreshAccessToken$(this.#keycloakService, this.#store).pipe(
      switchMap((token) => this.#openStream(conversationId, body, token))
    );
  }

  #openStream(conversationId: string, body: SendConversationInputRequest, token: string | null): Observable<AiStreamFrame> {
    return new Observable<AiStreamFrame>((subscriber) => {
      const controller = new AbortController();
      const traceId = this.#getTraceId(conversationId, body);
      const startedAt = Date.now();
      const outputByIndex = new Map<number, string>();
      let finishReason: string | undefined;
      let streamError: string | undefined;
      let waitingForToolConsent = false;
      let httpStatus: number | undefined;

      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      (async () => {
        try {
          const response = await fetch(`${this.#rootUrl}/api/v1/ai/conversations/${encodeURIComponent(conversationId)}/messages`, {
            method: 'POST',
            signal: controller.signal,
            headers,
            body: JSON.stringify(body),
          });
          httpStatus = response.status;

          if (!response.ok || !response.body) {
            const errorText = await response.text().catch(() => '');
            throw new Error(`HTTP ${response.status} ${response.statusText}: ${errorText || '(empty)'}`);
          }

          await readEventStream(
            response.body,
            (frame) => {
              this.#appendOutput(frame, outputByIndex);
              switch (frame.type) {
                case 'DONE':
                  finishReason = frame.finishReason;
                  break;
                case 'ERROR':
                  streamError = frame.message ?? 'AI assistant error';
                  break;
                case 'RUN_WAITING_FOR_TOOL_CONSENT':
                  waitingForToolConsent = true;
                  break;
              }
              subscriber.next(frame);
            },
            controller.signal
          );

          this.#captureGeneration({
            conversationId,
            body,
            traceId,
            startedAt,
            outputByIndex,
            finishReason,
            error: streamError,
            httpStatus,
          });
          if (!waitingForToolConsent) {
            this.#traceIdsByConversation.delete(conversationId);
          }
          subscriber.complete();
        } catch (error) {
          const err = error as Error;
          if (err?.name !== 'AbortError') {
            this.#captureGeneration({
              conversationId,
              body,
              traceId,
              startedAt,
              outputByIndex,
              error: err.message ?? 'AI assistant stream error',
              httpStatus,
            });
            this.#traceIdsByConversation.delete(conversationId);
            subscriber.error(err);
          } else {
            subscriber.complete();
          }
        }
      })();

      return () => {
        controller.abort();
      };
    });
  }

  #getTraceId(conversationId: string, body: SendConversationInputRequest): string {
    if (body.type === 'USER_MESSAGE') {
      const traceId = crypto.randomUUID();
      this.#traceIdsByConversation.set(conversationId, traceId);
      return traceId;
    }

    const traceId = this.#traceIdsByConversation.get(conversationId) ?? crypto.randomUUID();
    this.#traceIdsByConversation.set(conversationId, traceId);
    return traceId;
  }

  #appendOutput(frame: AiStreamFrame, outputByIndex: Map<number, string>): void {
    if (frame.type !== 'OUTPUT') {
      return;
    }

    const index = frame.index ?? 0;
    let output = outputByIndex.get(index) ?? '';
    if (frame.delta) {
      output += frame.delta;
    }
    if (frame.content && (!output || frame.content.startsWith(output) || !output.endsWith(frame.content))) {
      output = frame.content;
    }
    outputByIndex.set(index, output);
  }

  #captureGeneration({
    conversationId,
    body,
    traceId,
    startedAt,
    outputByIndex,
    finishReason,
    error,
    httpStatus,
  }: {
    conversationId: string;
    body: SendConversationInputRequest;
    traceId: string;
    startedAt: number;
    outputByIndex: Map<number, string>;
    finishReason?: string;
    error?: string;
    httpStatus?: number;
  }): void {
    const output = [...outputByIndex.entries()]
      .sort(([firstIndex], [secondIndex]) => firstIndex - secondIndex)
      .map(([, content]) => content)
      .join('\n');

    this.#posthogService.posthog.capture('$ai_generation', {
      $ai_trace_id: traceId,
      $ai_session_id: this.#toAiSessionId(conversationId),
      $ai_span_name: 'ai_assistant_response',
      $ai_latency: (Date.now() - startedAt) / 1000,
      $ai_stream: true,
      ...(body.type === 'USER_MESSAGE' ? { $ai_input: [{ role: 'user', content: body.message }] } : {}),
      ...(output ? { $ai_output_choices: [{ role: 'assistant', content: output }] } : {}),
      ...(finishReason ? { $ai_stop_reason: finishReason } : {}),
      ...(httpStatus !== undefined ? { $ai_http_status: httpStatus } : {}),
      ...(error ? { $ai_is_error: true, $ai_error: error } : {}),
    });
  }

  #toAiSessionId(conversationId: string): string {
    if (/^[A-Za-z0-9\-_~.@()!':|]+$/.test(conversationId)) {
      return conversationId;
    }

    return `conversation-${encodeURIComponent(conversationId).replaceAll('%', '_')}`;
  }
}
